import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import Tooltip from '../common/Tooltip';
import SingleSelect from '../common/SingleSelect';

// Pro badge component - extracted to avoid re-creating on every render
const ProBadge = ({ utm = 'wpuf-user-directory-advanced', config = {} }) => {
    const wpuf = window.wpuf_admin_script || {};
    const upgradeUrl = (wpuf.upgradeUrl || 'https://wedevs.com/wp-user-frontend-pro/pricing/') + '?utm_source=' + utm + '&utm_medium=pro-badge';

    return (
        <a
            href={upgradeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', pointerEvents: 'auto' }}
        >
            <img
                src={(config?.asset_url || window.wpuf_ud_free?.asset_url || '') + '/images/pro-badge.svg'}
                alt="Pro"
                style={{ width: '39px', height: '22px' }}
            />
        </a>
    );
};

const StepAdvanced = ({ formData, setFormData, config }) => {
    // Hover states for Pro features
    const [hoveredOption, setHoveredOption] = useState(null);

    // Free version options
    const freeAvatarSizes = ['128']; // Only 128 is free
    const freeSortOptions = ['id']; // Only User ID is free
    const freeGallerySizes = ['thumbnail']; // Only thumbnail is free

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'users_per_page') {
            setFormData(prev => ({
                ...prev,
                users_per_page: value
            }));
        } else if (name === 'max_item') {
            // Convert empty string to null, handle -1 specially, and ensure numeric values
            let numericValue;
            if (value === '' || value === null || value === undefined) {
                numericValue = null;
            } else {
                numericValue = parseInt(value);
                // Handle NaN case
                if (isNaN(numericValue)) {
                    numericValue = null;
                }
            }
            setFormData(prev => ({
                ...prev,
                max_item: numericValue
            }));
        } else if (name === 'default_sort_by') {
            setFormData(prev => ({
                ...prev,
                default_sort_by: value,
                orderby: value
            }));
        } else if (name === 'default_sort_order') {
            setFormData(prev => ({
                ...prev,
                default_sort_order: value,
                order: value
            }));
        } else if (name === 'avatar_size') {
            // Only allow free avatar sizes
            if (freeAvatarSizes.includes(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'profile_size') {
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (name === 'profile_base') {
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Number input style (shows native spinners like Pro)
    const numberInputStyle = {
        maxWidth: '793px',
        width: '100%',
        height: '42px',
        borderRadius: '6px',
        borderWidth: '1px',
        paddingTop: '9px',
        paddingRight: '13px',
        paddingBottom: '9px',
        paddingLeft: '13px',
        backgroundColor: '#FFFFFF',
        borderColor: '#CBD5E1',
        borderStyle: 'solid',
        opacity: 1,
        boxSizing: 'border-box',
        fontSize: '16px'
    };

    // Sort options with Pro status
    const sortOptions = [
        { value: 'id', label: __('User ID', 'wp-user-frontend'), isFree: true },
        { value: 'user_login', label: __('Username', 'wp-user-frontend'), isFree: false },
        { value: 'user_email', label: __('Email', 'wp-user-frontend'), isFree: false },
        { value: 'display_name', label: __('Display Name', 'wp-user-frontend'), isFree: false },
        { value: 'registered', label: __('Registration Date', 'wp-user-frontend'), isFree: false },
    ];

    // Avatar sizes with Pro status
    const avatarSizes = [
        { value: '32', label: '32×32', isFree: false },
        { value: '48', label: '48×48', isFree: false },
        { value: '80', label: '80×80', isFree: false },
        { value: '128', label: '128×128', isFree: true },
        { value: '160', label: '160×160', isFree: false },
        { value: '192', label: '192×192', isFree: false },
        { value: '256', label: '256×256', isFree: false },
    ];

    // Gallery image sizes with Pro status
    const gallerySizes = [
        { value: 'thumbnail', label: __('Thumbnail', 'wp-user-frontend'), isFree: true },
        { value: 'medium', label: __('Medium', 'wp-user-frontend'), isFree: false },
        { value: 'large', label: __('Large', 'wp-user-frontend'), isFree: false },
        { value: 'full', label: __('Full', 'wp-user-frontend'), isFree: false },
    ];

    // Social profiles options (all Pro)
    const socialProfiles = [
        { value: 'facebook', label: 'Facebook' },
        { value: 'twitter', label: 'X (Twitter)' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'instagram', label: 'Instagram' },
    ];

    // Profile permalink options (all free)
    const profileBases = [
        { value: 'username', label: __('Username', 'wp-user-frontend') },
        { value: 'user_id', label: __('User ID', 'wp-user-frontend') },
    ];

    return (
        <div className="wpuf-w-full wpuf-bg-white wpuf-max-w-3xl wpuf-mx-auto">
            <h2 className="wpuf-text-center"
                style={{
                    fontWeight: 400,
                    fontSize: '20px',
                    lineHeight: '36px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#000000'
                }}
            >
                {__('Advanced Control', 'wp-user-frontend')}
            </h2>
            <p className="wpuf-text-center wpuf-mt-2"
                style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#64748B'
                }}
            >{__('Manage how your user directory looks and behaves, from permalinks, socials and avatars display to sorting, for a clean, intuitive directory experience', 'wp-user-frontend')}</p>
            
            <div className="wpuf-mt-8">
                {/* Users Per Page - Available in Free */}
                <div className="wpuf-mt-[25px]">
                    <label htmlFor="users_per_page" className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Users Per Page', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Set how many users will be displayed per page in this directory', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    <input
                        type="number"
                        id="users_per_page"
                        name="users_per_page"
                        min="1"
                        max="100"
                        className="wpuf-block wpuf-min-w-full wpuf-m-0 wpuf-leading-none wpuf-text-gray-700 placeholder:wpuf-text-gray-400 wpuf-max-w-full focus:wpuf-ring-transparent"
                        style={numberInputStyle}
                        value={formData.users_per_page || 10}
                        onChange={handleChange}
                    />
                </div>

                {/* Number of Users in Directory - Available in Free */}
                <div className="wpuf-mt-[25px]">
                    <label htmlFor="max_item" className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Number of Users in Directory', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Set the maximum number of users to display in this directory. Enter -1 or leave empty to show all users.', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    <input
                        type="number"
                        id="max_item"
                        name="max_item"
                        className="wpuf-block wpuf-min-w-full wpuf-m-0 wpuf-leading-none wpuf-text-gray-700 placeholder:wpuf-text-gray-400 wpuf-max-w-full focus:wpuf-ring-transparent"
                        style={numberInputStyle}
                        value={formData.max_item || ''}
                        onChange={handleChange}
                        placeholder={__('Enter -1 for all users', 'wp-user-frontend')}
                    />
                </div>

                {/* Default Sort - Side by Side Layout (matching Pro) */}
                <div className="wpuf-mt-[25px]">
                    <label className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Default Sort', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Select the field by which users will be sorted by default and the sort order', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    <div className="wpuf-grid wpuf-grid-cols-2 wpuf-gap-3">
                        <SingleSelect
                            options={sortOptions}
                            value={formData.default_sort_by || formData.orderby || 'id'}
                            onChange={(value) => handleChange({ target: { name: 'default_sort_by', value } })}
                            ProBadge={() => <ProBadge utm="wpuf-user-directory-sort-by" config={config} />}
                        />
                        <SingleSelect
                            options={[
                                { value: 'asc', label: __('ASC', 'wp-user-frontend') },
                                { value: 'desc', label: __('DESC', 'wp-user-frontend') }
                            ]}
                            value={formData.default_sort_order || formData.order || 'desc'}
                            onChange={(value) => handleChange({ target: { name: 'default_sort_order', value } })}
                        />
                    </div>
                </div>

                {/* Profile Permalink Structure - All Free */}
                <div className="wpuf-mt-[25px]">
                    <label htmlFor="profile_base" className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Profile Permalink Structure', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Set how profile links are generated: use username (e.g., john-doe) for readable URLs or user ID (e.g., 123) for system-based links', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    <SingleSelect
                        options={profileBases}
                        value={formData.profile_base || 'username'}
                        onChange={(value) => handleChange({ target: { name: 'profile_base', value } })}
                    />
                </div>

                {/* Select Social Profiles - All Pro */}
                <div className="wpuf-mt-[25px]">
                    <div className="wpuf-flex wpuf-items-center wpuf-gap-2 wpuf-mb-3">
                        <label className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-m-0">
                            {__('Select Social Profiles', 'wp-user-frontend')}
                            <Tooltip className="wpuf-ml-2" content={__('Select the social media fields to display on user profiles', 'wp-user-frontend')}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                            </Tooltip>
                        </label>
                        <ProBadge utm="wpuf-user-directory-social-profiles" config={config} />
                    </div>
                    <div
                        className="wpuf-flex wpuf-flex-wrap wpuf-gap-2 wpuf-p-3 wpuf-rounded-lg wpuf-cursor-not-allowed wpuf-opacity-60"
                        style={{
                            border: '1px solid #E5E7EB',
                            backgroundColor: '#F9FAFB'
                        }}
                    >
                        {socialProfiles.map(profile => (
                            <span
                                key={profile.value}
                                className="wpuf-px-3 wpuf-py-1 wpuf-bg-gray-200 wpuf-text-gray-400 wpuf-rounded-md wpuf-text-sm"
                            >
                                {profile.label}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Profile Gallery Image Size - Thumbnail is free, rest are Pro */}
                <div className="wpuf-mt-[25px]">
                    <label className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Profile Gallery Image Size', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Select the default size for profile gallery images', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    <SingleSelect
                        options={gallerySizes}
                        value={formData.profile_size || 'thumbnail'}
                        onChange={(value) => handleChange({ target: { name: 'profile_size', value } })}
                        ProBadge={() => <ProBadge utm="wpuf-user-directory-gallery-size" config={config} />}
                    />
                </div>

                {/* Show Avatar in Table Layout - Pro feature, only visible for layout-1 */}
                {formData.directory_layout === 'layout-1' && (
                    <div className="wpuf-mt-[25px]">
                        <div className="wpuf-flex wpuf-items-center wpuf-gap-2 wpuf-mb-2">
                            <span className="wpuf-text-sm wpuf-font-medium wpuf-text-gray-700">
                                {__('Show avatar in table layout', 'wp-user-frontend')}
                            </span>
                            <ProBadge utm="wpuf-user-directory-show-avatar" config={config} />
                        </div>
                        <label
                            className="wpuf-flex wpuf-items-center wpuf-text-sm wpuf-text-gray-700 wpuf-cursor-not-allowed wpuf-opacity-60"
                            style={{
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #E5E7EB',
                                backgroundColor: '#F9FAFB'
                            }}
                        >
                            <div className="wpuf-relative">
                                <input
                                    type="checkbox"
                                    name="show_avatar_in_table"
                                    className="wpuf-sr-only"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                />
                                <div className="wpuf-w-5 wpuf-h-5 wpuf-border-2 wpuf-rounded wpuf-flex wpuf-items-center wpuf-justify-center wpuf-bg-gray-200 wpuf-border-gray-300">
                                    <svg className="wpuf-w-3 wpuf-h-3 wpuf-text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </label>
                    </div>
                )}

                {/* Avatar Size - Cards like Pro */}
                <div className="wpuf-mt-[25px]">
                    <label className="wpuf-flex wpuf-text-left wpuf-text-sm wpuf-font-medium wpuf-text-gray-700 wpuf-mb-3">
                        {__('Avatar Size', 'wp-user-frontend')}
                        <Tooltip className="wpuf-ml-2" content={__('Set the avatar size to display in directory layouts (this does not affect individual user profiles)', 'wp-user-frontend')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.375 9.375L9.40957 9.35771C9.88717 9.11891 10.4249 9.55029 10.2954 10.0683L9.70458 12.4317C9.57507 12.9497 10.1128 13.3811 10.5904 13.1423L10.625 13.125M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM10 6.875H10.0063V6.88125H10V6.875Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                        </Tooltip>
                    </label>
                    {/* Info box for recommended avatar size - Pro feature locked */}
                    {formData.directory_layout && (
                        <div className="wpuf-mb-3 wpuf-flex wpuf-items-center wpuf-gap-2">
                            <div
                                className="wpuf-flex wpuf-items-center wpuf-flex-1"
                                style={{
                                    maxWidth: '793px',
                                    height: '52px',
                                    borderRadius: '6px',
                                    opacity: 0.6,
                                    gap: '12px',
                                    padding: '16px',
                                    backgroundColor: '#EFF6FF'
                                }}
                            >
                                <svg className="wpuf-flex-shrink-0" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z" fill="#1E40AF"/>
                                </svg>
                                <span
                                    style={{
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        letterSpacing: '0%',
                                        color: '#1E40AF'
                                    }}
                                >
                                    {__('Recommended avatar size info available in Pro', 'wp-user-frontend')}
                                </span>
                            </div>
                            <ProBadge utm="wpuf-user-directory-avatar-info" config={config} />
                        </div>
                    )}
                    <div className="wpuf-flex wpuf-flex-wrap wpuf-gap-2">
                        {avatarSizes.map(size => {
                            const isSelected = (formData.avatar_size || '128') === size.value;
                            const isHovered = hoveredOption === `avatar-${size.value}`;

                            return (
                                <label
                                    key={size.value}
                                    className={`wpuf-relative wpuf-flex wpuf-flex-col wpuf-items-center wpuf-justify-center wpuf-w-[103px] wpuf-rounded-lg wpuf-transition-all wpuf-p-3 wpuf-pb-1 wpuf-gap-1 ${
                                        !size.isFree
                                            ? `wpuf-cursor-not-allowed wpuf-opacity-60 hover:wpuf-opacity-80 wpuf-bg-gray-50`
                                            : isSelected
                                                ? 'wpuf-bg-emerald-50 wpuf-text-emerald-600 wpuf-cursor-pointer'
                                                : 'wpuf-bg-white wpuf-text-gray-500 hover:wpuf-border-gray-400 wpuf-cursor-pointer'
                                    }`}
                                    style={{
                                        border: '1px solid',
                                        borderColor: !size.isFree ? '#E5E7EB' : (isSelected ? '#059669' : '#D1D5DB'),
                                    }}
                                    onMouseEnter={() => !size.isFree && setHoveredOption(`avatar-${size.value}`)}
                                    onMouseLeave={() => !size.isFree && setHoveredOption(null)}
                                >
                                    <input
                                        type="radio"
                                        name="avatar_size"
                                        value={size.value}
                                        checked={isSelected}
                                        onChange={handleChange}
                                        className="wpuf-sr-only"
                                        disabled={!size.isFree}
                                    />
                                    {/* Checkmark for selected free items */}
                                    {isSelected && size.isFree && (
                                        <span className="wpuf-absolute wpuf-top-2 wpuf-right-2">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width="20" height="20" rx="10" fill="#059669"/>
                                                <path d="M7.5 9.5L9.5 11.5L13 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </span>
                                    )}
                                    {/* Avatar Icon */}
                                    <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="wpuf-mb-1">
                                        <path
                                            d="M13.5676 14.604C12.4264 13.0979 10.6183 12.125 8.58301 12.125C6.54768 12.125 4.73958 13.0979 3.59839 14.604M13.5676 14.604C15.1109 13.2303 16.083 11.2287 16.083 9C16.083 4.85786 12.7251 1.5 8.58301 1.5C4.44087 1.5 1.08301 4.85786 1.08301 9C1.08301 11.2287 2.05509 13.2303 3.59839 14.604M13.5676 14.604C12.2426 15.7834 10.4965 16.5 8.58301 16.5C6.66954 16.5 4.92343 15.7834 3.59839 14.604M11.083 7.125C11.083 8.50571 9.96372 9.625 8.58301 9.625C7.2023 9.625 6.08301 8.50571 6.08301 7.125C6.08301 5.74429 7.2023 4.625 8.58301 4.625C9.96372 4.625 11.083 5.74429 11.083 7.125Z"
                                            stroke={!size.isFree ? "#9CA3AF" : isSelected ? "#059669" : "#9CA3AF"}
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className={`wpuf-text-xs wpuf-text-center wpuf-leading-tight ${!size.isFree ? 'wpuf-text-gray-400' : ''}`}>
                                        {size.label}
                                    </div>
                                    {/* Pro Badge on hover only */}
                                    {!size.isFree && isHovered && (
                                        <div className="wpuf-absolute wpuf-inset-0 wpuf-flex wpuf-items-center wpuf-justify-center wpuf-bg-white/90 wpuf-rounded-lg">
                                            <ProBadge utm="wpuf-user-directory-avatar-size" config={config} />
                                        </div>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StepAdvanced;
